import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Banner, Button, Chip, DataTable, EmptyState, SegmentedButton, ServerCard, StatusChip, TextField, TopAppBar,
} from '@meshble/molo';
import { type Instance, type Server } from '../lib/api';
import { contaIstanze } from '../lib/copie';
import { avvisiDelServer, riassuntoAvvisi, useAvvisi, useIstanze, useServers, type Avviso } from '../lib/dati';
import { daFare, filtraServer, majorDi, type Chip as ChipFiltro, type VistaServer } from '../lib/filtro-server';
import { faseContatto, nomePassoServer, quandoRelativo } from '../lib/attivita';
import { CaricoSezione, Logo, LogoAvanzamento } from '../brand';
import { BarraUtente } from '../shell';
import { usePermessi } from '../session';
import { Cloud, Plus, Warn } from '../icons';
import { t } from '../lib/lingua';
import { ConPermesso } from '../permesso';

export { useServers } from '../lib/dati';

/** Dove resta la vista scelta: una preferenza di chi guarda, nel suo browser. */
const VISTA = 'molo.server.vista';
function leggiVista(): string | null {
  try { return localStorage.getItem(VISTA); } catch { return null; }
}

/**
 * I server dell'organizzazione.
 *
 * Ogni scheda risponde alle tre domande che ci si fa davanti a una lista di
 * macchine: come sta, cosa ospita, da quanto. L'indirizzo c'è ma non è la
 * prima cosa: chi guarda cerca «il server di acme», non un IP.
 */
export function ServersPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/servers' });
  const navigate = useNavigate();
  const q = useServers(slug);
  const istanze = useIstanze(slug);
  const avvisi = useAvvisi(slug);
  const servers = q.data?.servers ?? [];
  // Creare e collegare server è dell'owner: sono i soldi (blocco 33).
  const permessi = usePermessi(slug);

  // Ricerca, chip e vista: con dieci macchine si guarda, con quaranta si
  // cerca. La vista (schede o lista) è una preferenza di chi guarda e resta
  // nel browser; ricerca e chip si azzerano a ogni apertura, di proposito.
  const [cerca, setCerca] = useState('');
  const [chips, setChips] = useState<Set<ChipFiltro>>(new Set());
  const [vista, setVista] = useState<'schede' | 'lista'>(() => (leggiVista() === 'lista' ? 'lista' : 'schede'));
  const cambiaVista = (v: string) => {
    setVista(v === 'lista' ? 'lista' : 'schede');
    try { localStorage.setItem(VISTA, v); } catch { /* privato o pieno: si perde la preferenza, non la pagina */ }
  };
  const alterna = (c: ChipFiltro) => setChips((prima) => {
    const dopo = new Set(prima);
    if (dopo.has(c)) dopo.delete(c); else dopo.add(c);
    return dopo;
  });

  const viste: VistaServer[] = servers.map((s) => ({
    server: s,
    istanze: (istanze.data?.instances ?? []).filter((i) => i.serverId === s.id),
    avvisi: avvisiDelServer(avvisi.data, s.id).accesi.length,
  }));
  const trovate = filtraServer(viste, cerca, chips);
  const perServer = new Map(viste.map((v) => [v.server.id, v.istanze]));

  return (
    <>
      {/* L'azione principale sta nella pagina che la riguarda, come «Nuova
          istanza» nella sua: la rail è solo navigazione. */}
      <TopAppBar
        title={t('Server')}
        actions={
          <>
            <ConPermesso ha={permessi} serve="server.create">
              <Button variant="filled" icon={<Plus />} onClick={() => navigate({ to: '/orgs/$slug/servers/new', params: { slug } })}>
                {t('Collega un server')}
              </Button>
            </ConPermesso>
            <BarraUtente slug={slug} />
          </>
        }
      />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        {q.error && <Banner tone="error" title={t('Non riesco a leggere i server')}>{q.error.message}</Banner>}

        {q.isSuccess && servers.length === 0 && (
          <EmptyState
            icon={<Cloud />}
            title={t('Nessun server ancora')}
            action={
              <ConPermesso ha={permessi} serve="server.create">
                <Button
                  variant="filled"
                  icon={<Plus />}
                  onClick={() => navigate({ to: '/orgs/$slug/servers/new', params: { slug } })}
                >
                  {t('Collega il primo server')}
                </Button>
              </ConPermesso>
            }
          >
            {t('Un server è la macchina su cui girano le istanze Odoo. Creala dal pannello del tuo fornitore (OVH, Google Cloud, Azure, o in sede), poi collegala qui: all\'installazione pensa Molo.')}
          </EmptyState>
        )}

        {servers.length > 0 && (
          <div className="md-filtri-server">
            <TextField
              label=""
              aria-label={t('Cerca un server')}
              placeholder={t('Cerca per nome, indirizzo, versione di Odoo, istanza…')}
              value={cerca}
              onChange={(e) => setCerca(e.target.value)}
              className="md-filtri-server__cerca"
            />
            <div className="md-filtri-server__chip" role="group" aria-label={t('Filtri')}>
              <Chip selected={chips.has('aggiornare')} onClick={() => alterna('aggiornare')}>{t('Da aggiornare')}</Chip>
              <Chip selected={chips.has('avvisi')} onClick={() => alterna('avvisi')}>{t('Con avvisi')}</Chip>
              <Chip selected={chips.has('nonPronti')} onClick={() => alterna('nonPronti')}>{t('Non pronti')}</Chip>
            </div>
            <SegmentedButton
              aria-label={t('Vista')}
              value={vista}
              onValueChange={cambiaVista}
              options={[{ value: 'schede', label: t('Schede') }, { value: 'lista', label: t('Lista') }]}
            />
          </div>
        )}

        {servers.length > 0 && trovate.length === 0 && (
          <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {cerca.trim()
              ? chips.size > 0
                ? t('Nessun server corrisponde a «{cerca}» con questi filtri.', { cerca: cerca.trim() })
                : t('Nessun server corrisponde a «{cerca}».', { cerca: cerca.trim() })
              : t('Nessun server corrisponde con questi filtri.')}
          </p>
        )}

        {vista === 'lista' && trovate.length > 0 && (
          <DataTable
            columns={[
              { key: 'nome', header: 'Server' },
              { key: 'indirizzo', header: t('Indirizzo') },
              { key: 'odoo', header: 'Odoo' },
              { key: 'istanze', header: t('Istanze'), numeric: true },
              { key: 'stato', header: t('Stato') },
              { key: 'daFare', header: t('Da fare') },
              { key: 'collegato', header: t('Collegato'), nowrap: true },
            ]}
            rowKey={(r) => String(r.id)}
            onRowClick={(r) => navigate({ to: '/orgs/$slug/servers/$id', params: { slug, id: String(r.id) } })}
            rows={trovate.map((v) => {
              const s = v.server;
              const cose = daFare(v);
              return {
                id: s.id,
                nome: <span className="md-body-large">{s.name}</span>,
                indirizzo: <span className="md-mono">{s.ipv4 || '—'}</span>,
                odoo: majorDi(v).join(', ') || '—',
                // Le copie di prova a parte: «3 · 1 copia», non «4».
                istanze: contaIstanze(v.istanze, true),
                stato: <StatusChip status={s.status} />,
                daFare: v.avvisi > 0 || cose.length > 0
                  ? <span className="md-server-avvisi__accesi">{[...(v.avvisi > 0 ? [v.avvisi === 1 ? t('1 avviso') : t('{n} avvisi', { n: v.avvisi })] : []), ...cose].join(' · ')}</span>
                  : <span style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>{t('Niente')}</span>,
                collegato: quandoRelativo(s.createdAt),
              };
            })}
          />
        )}

        <div className="md-griglia" hidden={vista !== 'schede'}>
          {trovate.map(({ server: s }) => {
            const suoi = avvisiDelServer(avvisi.data, s.id);
            return (
            <ServerCard
              key={s.id}
              name={s.name}
              spec={<Riga server={s} istanze={perServer.get(s.id) ?? []} />}
              status={s.status}
              // Un server con un avviso acceso si vede dal bordo come uno
              // fallito: nella lista lo si nota prima di leggere.
              className={suoi.accesi.length > 0 ? 'md-card--error-outline' : undefined}
              // Il molo che cresce prende il posto della barra: nella riga c'è
              // già il numero dei passi, e due indicatori che dicono la stessa
              // cosa sono uno di troppo. Con tre server in installazione la
              // lista mostra tre moli a stadi diversi, fermi.
              footer={
                <>
                  <Avanzamento server={s} />
                  <RigaAvvisi {...suoi} />
                </>
              }
              error={s.status === 'error' ? t('L’installazione si è fermata: apri il server per riprenderla.') : undefined}
              onClick={() => navigate({ to: '/orgs/$slug/servers/$id', params: { slug, id: s.id } })}
            />
            );
          })}
        </div>
      </div>
    </>
  );
}

function Riga({ server: s, istanze }: { server: Server; istanze: Instance[] }) {
  return (
    <span className="md-server-riga">
      <span className="md-mono">{s.ipv4 || t('senza indirizzo')}</span>
      {s.status === 'ready' && (
        <span>{contaIstanze(istanze)}</span>
      )}
      <span>{t('Collegato {quando}', { quando: quandoRelativo(s.createdAt) })}</span>
    </span>
  );
}

/** Cosa sta succedendo a un server che non è pronto, con il segno che lo dice. */
function Avanzamento({ server: s }: { server: Server }) {
  if (s.status === 'installing_stack' && s.totalSteps > 0) {
    const step = s.progress ?? 0;
    return (
      <div className="md-avanzamento md-avanzamento--riga">
        <LogoAvanzamento step={step} total={s.totalSteps} size={34} />
        <span>{nomePassoServer(step, s.totalSteps)}</span>
        <span>{step}/{s.totalSteps}</span>
      </div>
    );
  }
  if (s.status === 'bootstrapping') {
    return (
      <div className="md-avanzamento md-avanzamento--riga">
        <Logo size={34} anim="marea" label={t('In attesa del comando')} />
        <span>{t('Aspetto il comando sulla macchina')}</span>
      </div>
    );
  }
  if (s.status === 'creating' || s.status === 'waiting_ip' || s.status === 'waiting_ssh' || s.status === 'verifying') {
    return (
      <div className="md-avanzamento md-avanzamento--riga">
        <Logo size={34} anim="ormeggio" label={t('Contatto in corso')} />
        <span>{faseContatto(s.status, s.provider, s.failoverIpOrdered)}</span>
      </div>
    );
  }
  if (s.status === 'error' && s.totalSteps > 0) {
    return (
      <div className="md-avanzamento md-avanzamento--riga">
        <LogoAvanzamento step={s.progress ?? 0} total={s.totalSteps} size={34} failed />
        <span>{t('Ferma a «{passo}»', { passo: nomePassoServer(s.progress ?? 0, s.totalSteps) })}</span>
        <span>{s.progress ?? 0}/{s.totalSteps}</span>
      </div>
    );
  }
  return null;
}


/**
 * Gli avvisi di un server, in una riga sulla sua scheda: i titoli, e quante
 * volte, perché tre certificati in scadenza sono un problema solo. Il
 * dettaglio — quale certificato, da quando — sta nella schermata del server:
 * qui si decide se aprirla.
 */
function RigaAvvisi({ accesi, inArrivo }: { accesi: Avviso[]; inArrivo: Avviso[] }) {
  if (accesi.length === 0 && inArrivo.length === 0) return null;
  return (
    <div className="md-server-avvisi md-body-small">
      {accesi.length > 0 && (
        <span className="md-server-avvisi__accesi"><Warn />{riassuntoAvvisi(accesi)}</span>
      )}
      {inArrivo.length > 0 && (
        <span>{inArrivo.length === 1 ? t('1 cosa da tenere d’occhio') : t('{n} cose da tenere d’occhio', { n: inArrivo.length })}</span>
      )}
    </div>
  );
}
